import type { CountersResponse } from '@prerender/diagnostics-api';

import CounterCard from './CounterCard';
import { formatDateWithAge } from './formatDateWithAge';
import type { Loadable } from './loadable';
import SeverityCountsList from './SeverityCountsList';

type IncidentCountersViewProps = {
  counters: Loadable<CountersResponse>;
  now: Date;
};

const NEVER_RAN = 'Detection has not run yet';

const FILTERS_DO_NOT_CHANGE_THIS = 'Filtering the list does not change this.';

const FILTERS_DO_NOT_CHANGE_THESE = 'Filtering the list does not change these.';

const lastRunCaption = (lastRunAt: string | null, now: Date): string => {
  if (lastRunAt === null) {
    return NEVER_RAN;
  }

  return `Detection last ran ${formatDateWithAge(new Date(lastRunAt), now)}`;
};

type CounterFiguresProps = {
  counters: CountersResponse;
  now: Date;
};

function CounterFigures({ counters, now }: CounterFiguresProps) {
  return (
    <>
      <p className="diagnostics-counters-caption">{lastRunCaption(counters.lastRunAt, now)}</p>
      <div className="diagnostics-counter-cards">
        <CounterCard
          title="Flagged accounts"
          hint={`Accounts with at least one open incident. ${FILTERS_DO_NOT_CHANGE_THIS}`}
        >
          <p className="diagnostics-counter-figure">{counters.flaggedAccounts}</p>
        </CounterCard>
        <CounterCard
          title="Open incidents"
          hint={`Incidents that are active or monitoring. ${FILTERS_DO_NOT_CHANGE_THIS}`}
        >
          <p className="diagnostics-counter-figure">{counters.openIncidents}</p>
        </CounterCard>
        <CounterCard
          title="Open by severity"
          hint={`Open incidents grouped by severity. ${FILTERS_DO_NOT_CHANGE_THESE}`}
        >
          <SeverityCountsList counts={counters.openBySeverity} />
        </CounterCard>
      </div>
    </>
  );
}

function IncidentCountersView({ counters, now }: IncidentCountersViewProps): JSX.Element | null {
  if (counters.status === 'loading') {
    return null;
  }

  return (
    <div className="diagnostics-counters" role="group" aria-label="Incident counters">
      {counters.status !== 'loaded' ? (
        <p className="diagnostics-counters-error">Counters unavailable</p>
      ) : (
        <CounterFigures counters={counters.data} now={now} />
      )}
    </div>
  );
}

export default IncidentCountersView;
